import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const TagModal = ({
  open,
  onClose,
  onSubmit,
  isSubmitting,
  testCaseResultId,
  existingTags = "",
  existingComment = "",
  isLoading = false,
}) => {
  const [tag, setTag] = useState(existingTags);
  const [comment, setComment] = useState(existingComment);
  const [errors, setErrors] = useState({ tag: "", comment: "" });

  // Update state when props change
  useEffect(() => {
    setTag(existingTags);
    setComment(existingComment);
  }, [existingTags, existingComment]);

  const validateInputs = () => {
    const newErrors = { tag: "", comment: "" };
    let isValid = true;

    if (!tag.trim()) {
      newErrors.tag = "Tag is required";
      isValid = false;
    } else if (!tag.startsWith("#")) {
      setTag("#" + tag);
    }

    if (!comment.trim()) {
      newErrors.comment = "Comment is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = () => {
    if (validateInputs()) {
      onSubmit(testCaseResultId, {
        tag: tag.trim(),
        comment: comment.trim(),
      });
    }
  };

  const handleClose = () => {
    setTag("");
    setComment("");
    setErrors({ tag: "", comment: "" });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6">
            {isLoading ? "Loading Tags..." : "Add Tag & Comment"}
          </Typography>
          <IconButton edge="end" color="inherit" onClick={handleClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress size={30} />
          </Box>
        ) : (
          <Box sx={{ mb: 3, mt: 1 }}>
            <TextField
              fullWidth
              label="Tag"
              value={tag}
              onChange={(e) => {
                const value = e.target.value;
                setTag(value.startsWith("#") ? value : "#" + value);
              }}
              error={Boolean(errors.tag)}
              helperText={errors.tag || "Tags should start with # (added automatically)"}
              placeholder="#bug"
              sx={{ mb: 3 }}
            />
            <TextField
              fullWidth
              label="Comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              error={Boolean(errors.comment)}
              helperText={errors.comment}
              placeholder="Describe the reason for this tag"
              multiline
              rows={3}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting || isLoading}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
        >
          {isSubmitting ? "Adding..." : "Add Tag"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TagModal;
